import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import AppShell from "./components/layout/AppShell";
import Login from "./pages/Login";
import ExecutiveSummary from "./pages/ExecutiveSummary";
import ProjectCashFlow from "./pages/ProjectCashFlow";
import Reconciliation from "./pages/Reconciliation";
import Tasks from "./pages/Tasks";
import Activity from "./pages/Activity";
import Settings from "./pages/Settings";
import { FullScreenSpinner } from "./components/ui/Spinner";
import type { PageKey } from "@shared/types";

const PAGE_PATH: Record<PageKey, string> = {
  summary: "/",
  "cash-flow": "/cash-flow",
  reconciliation: "/reconciliation",
  tasks: "/tasks",
  activity: "/activity",
};

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

// Redirect away from a page the admin has hidden from this client.
function RequirePage({ page, children }: { page: PageKey; children: JSX.Element }) {
  const { canSee, visiblePages } = useAuth();
  if (canSee(page)) return children;
  const fallback = visiblePages[0] ? PAGE_PATH[visiblePages[0]] : "/settings";
  return <Navigate to={fallback} replace />;
}

export default function App() {
  const { user, loading } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={loading ? <FullScreenSpinner /> : user ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<RequirePage page="summary"><ExecutiveSummary /></RequirePage>} />
        <Route path="/cash-flow" element={<RequirePage page="cash-flow"><ProjectCashFlow /></RequirePage>} />
        <Route path="/cash-flow/:projectId" element={<RequirePage page="cash-flow"><ProjectCashFlow /></RequirePage>} />
        <Route path="/reconciliation" element={<RequirePage page="reconciliation"><Reconciliation /></RequirePage>} />
        <Route path="/reconciliation/:projectId" element={<RequirePage page="reconciliation"><Reconciliation /></RequirePage>} />
        <Route path="/tasks" element={<RequirePage page="tasks"><Tasks /></RequirePage>} />
        <Route path="/activity" element={<RequirePage page="activity"><Activity /></RequirePage>} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
